import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2, Sparkles, Play, RotateCcw } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { fmtDateTime } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

// Run the second reader. The website only records the request; ai_review.py (systemd timer, every
// 5 minutes) submits the batches and collects the answers, so the API key never reaches the website.

const fmtNum = (n) => (n >= 1e6 ? `${(n / 1e6).toFixed(1)} M` : n >= 1e3 ? `${Math.round(n / 1e3)} k` : String(n || 0));

export default function AiAdmin() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [confirm, setConfirm] = useState(null); // "new" | "all"
  const [sending, setSending] = useState(false);

  const load = useCallback(() => api.get("/ai-reviews").then((r) => setData(r.data)).catch(() => setData(null)), []);
  useEffect(() => { load(); }, [load]);
  // while something is waiting or running, refresh every 30 s
  const busy = data && (data.requests.some((r) => ["requested", "started"].includes(r.status))
    || data.batches.some((b) => b.status === "in_progress"));
  useEffect(() => {
    if (!busy) return undefined;
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [busy, load]);

  if (!data) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  const total = data.applications.length;
  const reviewed = data.applications.filter((a) => a.ai).length;
  const pending = data.counts.pending || 0;
  const failed = data.counts.failed || 0;
  const toSend = confirm === "all" ? total : total - reviewed;

  const run = async () => {
    setSending(true);
    try {
      const res = await api.post("/ai-reviews/run", { scope: confirm });
      toast.success(res.data.message);
      setConfirm(null);
      load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const Stat = ({ value, label }) => (
    <div className="bg-card border border-border rounded-xl p-4">
      <p className="font-heading text-2xl font-semibold text-primary">{value}</p>
      <p className="text-xs text-muted-foreground mt-1">{label}</p>
    </div>
  );

  return (
    <div className="animate-fade-in max-w-5xl" data-testid="ai-admin-page">
      <p className="text-sm text-muted-foreground uppercase tracking-[0.2em] mb-1">{t("admin.ai.page.eyebrow")}</p>
      <h1 className="font-heading text-3xl font-semibold tracking-tight flex items-center gap-3">
        <Sparkles className="w-7 h-7 text-primary" /> {t("admin.ai.page.title")}
      </h1>
      <p className="text-muted-foreground text-sm mt-2 max-w-3xl leading-relaxed">{t("admin.ai.page.subtitle")}</p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 my-6">
        <Stat value={total} label={t("admin.ai.page.stat_total")} />
        <Stat value={reviewed} label={t("admin.ai.page.stat_done")} />
        <Stat value={pending} label={t("admin.ai.page.stat_pending")} />
        <Stat value={failed} label={t("admin.ai.page.stat_failed")} />
      </div>

      <div className="bg-card border border-border rounded-xl p-6 mb-6">
        <div className="flex flex-wrap gap-3">
          <Button onClick={() => setConfirm("new")} disabled={!data.can_run || busy || total === reviewed} data-testid="ai-run-new">
            <Play className="w-4 h-4 mr-2" /> {t("admin.ai.page.run_new", { count: total - reviewed })}
          </Button>
          <Button variant="outline" onClick={() => setConfirm("all")} disabled={!data.can_run || busy || total === 0} data-testid="ai-run-all">
            <RotateCcw className="w-4 h-4 mr-2" /> {t("admin.ai.page.run_all", { count: total })}
          </Button>
        </div>
        {!data.can_run && <p className="text-xs text-muted-foreground mt-3">{t("admin.ai.page.before_deadline", { deadline: data.deadline })}</p>}
        {busy && <p className="text-xs text-primary mt-3 flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> {t("admin.ai.page.busy")}</p>}
        <p className="text-xs text-muted-foreground mt-3">
          {t("admin.ai.page.tokens", { input: fmtNum(data.tokens.input), output: fmtNum(data.tokens.output), cache: fmtNum(data.tokens.cache_read) })}
          {" "}{t("admin.ai.page.cost_hint")}
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <h3 className="font-heading text-base font-medium px-5 pt-4 pb-2">{t("admin.ai.page.requests_title")}</h3>
          {data.requests.length === 0 ? <p className="text-sm text-muted-foreground px-5 pb-5">{t("admin.ai.page.none")}</p> : (
            <table className="w-full text-sm">
              <tbody>
                {data.requests.map((r) => (
                  <tr key={r.id} className="border-t border-border">
                    <td className="py-2.5 px-5 text-muted-foreground whitespace-nowrap">{fmtDateTime(r.created_at)}</td>
                    <td className="py-2.5 px-2">{t(`admin.ai.page.scope_${r.scope}`)}</td>
                    <td className="py-2.5 px-2 text-muted-foreground truncate max-w-[160px]">{r.requested_by}</td>
                    <td className={`py-2.5 px-5 text-right ${r.status === "failed" ? "text-destructive" : ""}`} title={r.message || ""}>{t(`admin.ai.page.req_${r.status}`)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <h3 className="font-heading text-base font-medium px-5 pt-4 pb-2">{t("admin.ai.page.batches_title")}</h3>
          {data.batches.length === 0 ? <p className="text-sm text-muted-foreground px-5 pb-5">{t("admin.ai.page.none")}</p> : (
            <table className="w-full text-sm">
              <tbody>
                {data.batches.map((b) => (
                  <tr key={b.anthropic_id} className="border-t border-border">
                    <td className="py-2.5 px-5 text-muted-foreground whitespace-nowrap">{fmtDateTime(b.created_at)}</td>
                    <td className="py-2.5 px-2">{t("admin.ai.page.batch_size", { count: b.n_requests })}</td>
                    <td className="py-2.5 px-2 text-xs text-muted-foreground">{b.model}</td>
                    <td className="py-2.5 px-5 text-right">{t(`admin.ai.page.batch_${b.status}`)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("admin.ai.page.confirm_title")}</DialogTitle>
            <DialogDescription className="leading-relaxed pt-2">{t("admin.ai.page.confirm_text", { count: toSend })}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)}>{t("common.cancel")}</Button>
            <Button onClick={run} disabled={sending} data-testid="ai-run-confirm">
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />} {t("admin.ai.page.confirm_btn")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
